/**
 * Mojo Up AI Cloud's half of sign-in: no MSAL, Cloud's own tokens (cloudSession.ts), and the person's
 * organisations from `/cloud/v1/me`. The active organisation is remembered in localStorage and
 * named on every API request; the people pickers search its members instead of Microsoft Graph.
 */

import type { CloudMe, DiscoveryDocument } from '@aioe/shared';
import { type ReactNode, useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { useNavigate } from 'react-router-dom';

import { routes, setOrganisation } from '../api/client.ts';
import { setMemberDirectory } from '../directory.ts';
import { AuthContext, type AuthState, SIGNED_OUT_PATH } from './context.ts';
import { cloudSession } from './cloudSession.ts';

const ORGANISATION_KEY = 'aioe.cloud.organisation';

function storedOrganisation(): string | undefined {
  try {
    return localStorage.getItem(ORGANISATION_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}
function storeOrganisation(id: string): void {
  try {
    localStorage.setItem(ORGANISATION_KEY, id);
  } catch {
    /* storage unavailable: the choice lasts as long as the page */
  }
}

export function CloudAuthProvider({ discovery, children }: { discovery: DiscoveryDocument; children: ReactNode }) {
  const navigate = useNavigate();
  const signedIn = useSyncExternalStore(cloudSession.subscribe, cloudSession.signedIn);
  // A new tab (remote control, a link opened beside this one) first asks the others for their session.
  const [settled, setSettled] = useState(signedIn);
  useEffect(() => {
    let live = true;
    void cloudSession.settled().then(() => live && setSettled(true));
    return () => {
      live = false;
    };
  }, []);
  const [loaded, setMe] = useState<CloudMe>();
  const [error, setError] = useState<string>();
  const [chosen, setChosen] = useState<string | undefined>(storedOrganisation);

  const reload = useCallback(async () => {
    const next = await routes.cloudMe();
    setMe(next);
    setError(undefined);
    return next;
  }, []);
  // What was loaded belongs to a sign-in; signed out, there is no one.
  const me = signedIn ? loaded : undefined;

  useEffect(() => {
    if (!signedIn) return;
    routes
      .cloudMe()
      .then((next) => {
        setMe(next);
        setError(undefined);
      })
      .catch((e: Error) => {
        // A sign-in that could not be renewed has already sent the person to the sign-in page.
        if (cloudSession.signedIn()) setError(e.message);
      });
  }, [signedIn]);

  // The organisation chosen, if the person is still in it; otherwise their personal one (listed first).
  const organisation = me ? (me.organisations.find((o) => o.id === chosen) ?? me.organisations[0]) : undefined;
  // Set before the views render, so their first request already names it.
  setOrganisation(organisation?.id);

  useEffect(() => {
    if (!organisation) return;
    storeOrganisation(organisation.id);
    const id = organisation.id;
    let members: Promise<Array<{ userId: string; name?: string; email?: string }>> | undefined;
    setMemberDirectory(() => (members ??= routes.organisationMembers(id).then((r) => r.members).catch(() => [])));
  }, [organisation]);

  const switchTo = useCallback(
    (id: string) => {
      setOrganisation(id);
      storeOrganisation(id);
      setChosen(id);
      // A record's own page belongs to the organisation it came from: go to its list instead.
      const [, top, detail] = location.pathname.split('/');
      if (detail && top !== 'invite') navigate(`/${top}`, { replace: true });
    },
    [navigate],
  );

  const value = useMemo<AuthState>(() => {
    // Signing out lands on the signed-out page, which says so, rather than on a sign-in page that greets a newcomer.
    const signOut = (opts?: { idle?: boolean }) => {
      void cloudSession.signOut();
      navigate(`${SIGNED_OUT_PATH}${opts?.idle ? '?idle=1' : ''}`, { replace: true });
    };
    if (error) return { ready: false, error, discovery, signOut };
    if (!signedIn && !settled) return { ready: false, discovery, signOut };
    if (!signedIn) return { ready: true, discovery, signedOut: true, signOut };
    if (!me || !organisation) return { ready: false, discovery, signOut };
    return {
      ready: true,
      discovery,
      // `picture` arrives with the API's newer CloudMe; optional until every deployment has it.
      account: { name: me.name, email: me.email, id: me.id, ...((me as CloudMe & { picture?: string }).picture ? { picture: (me as CloudMe & { picture?: string }).picture } : {}) },
      cloud: { me, organisation, switchTo, reload },
      signOut,
    };
  }, [error, signedIn, settled, me, organisation, discovery, switchTo, reload, navigate]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
